import { useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    async function checkAuth() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          const currentPath = location.pathname + location.search;
          navigate(`/auth/signin?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
          return;
        }
        setIsAuthenticated(true);
      } catch (error) {
        logger.error('Auth check failed', error instanceof Error ? error : new Error(String(error)));
        const currentPath = location.pathname + location.search;
        navigate(`/auth/signin?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
      } finally {
        setIsLoading(false);
      }
    }

    checkAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT' || !session) {
          const currentPath = location.pathname + location.search;
          navigate(`/auth/signin?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
        } else if (session) {
          setIsAuthenticated(true);
          setIsLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : null;
}